import React, { FormEvent, useCallback, useEffect, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Snackbar,
    TextField,
    Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import axiosInstance from "../AxiosInstance";
import { useAuth } from "../AuthContext";

interface MfaStatus {
    enabled: boolean;
    enrolledAtUtc?: string | null;
    recoveryCodesRemaining?: number | null;
    graceEndsAtUtc?: string | null;
}

interface CurrentOrganisation {
    name: string;
    startMonth: number;
    startDayOfWeek: number;
    fiscalCalendarRuleSet: number;
}

const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const FISCAL_CALENDAR_LABELS: Record<number, string> = {
    0: "Calendar month (majority days)",
    1: "5-4-4 retail calendar",
    2: "Start month (min 3 days in first week)",
};

const formatStartMonth = (startMonth: number): string => {
    if (startMonth >= 1 && startMonth <= 12) {
        return MONTH_NAMES[startMonth - 1];
    }

    return "—";
};

const formatStartDayOfWeek = (value: number): string => {
    if (value === 7) {
        return "Sunday";
    }

    if (value >= 0 && value <= 6) {
        return DAY_NAMES[value];
    }

    return "—";
};

const formatFiscalCalendar = (value: number): string => FISCAL_CALENDAR_LABELS[value] ?? "—";

const ProfileField = ({ label, value }: { label: string; value: string }) => (
    <Box sx={{ py: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="body1">{value}</Typography>
    </Box>
);

function ProfilePage() {
    const navigate = useNavigate();
    const auth = useAuth();
    const email = auth?.email || "—";
    const organisationName = auth?.organisation || "—";

    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);
    const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);
    const [successMessage, setSuccessMessage] = useState("Done.");

    const [mfaStatus, setMfaStatus] = useState<MfaStatus | null>(null);
    const [mfaLoadError, setMfaLoadError] = useState("");
    const [manageMfaOpen, setManageMfaOpen] = useState(false);
    const [enabledBannerDismissed, setEnabledBannerDismissed] = useState(false);
    const [regenCode, setRegenCode] = useState("");
    const [newRecoveryCodes, setNewRecoveryCodes] = useState<string[] | null>(null);

    const [organisationSettings, setOrganisationSettings] = useState<CurrentOrganisation | null>(null);
    const [organisationLoadError, setOrganisationLoadError] = useState("");

    const loadMfaStatus = useCallback(async () => {
        try {
            const { data } = await axiosInstance.get<MfaStatus>("/mfa/status");
            setMfaStatus(data);
            setMfaLoadError("");
        } catch {
            setMfaLoadError("Could not load MFA status.");
            setMfaStatus(null);
        }
    }, []);

    const loadOrganisation = useCallback(async () => {
        try {
            const { data } = await axiosInstance.get<CurrentOrganisation>("/organisation/current");
            setOrganisationSettings(data);
            setOrganisationLoadError("");
        } catch {
            setOrganisationLoadError("Could not load organisation details.");
            setOrganisationSettings(null);
        }
    }, []);

    useEffect(() => {
        void loadMfaStatus();
        void loadOrganisation();
    }, [loadMfaStatus, loadOrganisation]);

    const resetPasswordFields = () => {
        setOldPassword("");
        setNewPassword("");
        setConfirmNewPassword("");
    };

    const handleChangePasswordClose = () => {
        setChangePasswordOpen(false);
        resetPasswordFields();
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (newPassword !== confirmNewPassword) {
            setErrorMessage("New passwords do not match.");
            setErrorSnackbarOpen(true);
            return;
        }

        try {
            const response = await axiosInstance.post("/auth/change-password", {
                oldPassword,
                newPassword,
            });

            if (response.status === 200) {
                setSuccessMessage("Password changed successfully.");
                setSuccessSnackbarOpen(true);
                handleChangePasswordClose();
            }
        } catch {
            setErrorMessage("Failed to change password.");
            setErrorSnackbarOpen(true);
        }
    };

    const handleManageMfaClose = () => {
        setManageMfaOpen(false);
        setRegenCode("");
        setNewRecoveryCodes(null);
    };

    const handleRegenCodes = async () => {
        try {
            const { data } = await axiosInstance.post("/mfa/recovery-codes/regenerate", { code: regenCode.trim() });
            setRegenCode("");
            setNewRecoveryCodes(data.recoveryCodes || []);
            await loadMfaStatus();
        } catch {
            setErrorMessage("Failed to regenerate codes.");
            setErrorSnackbarOpen(true);
        }
    };

    const graceDeadline =
        mfaStatus?.graceEndsAtUtc && !mfaStatus.enabled
            ? new Date(mfaStatus.graceEndsAtUtc).toLocaleString()
            : null;
    const showEnabledBanner = Boolean(mfaStatus?.enabled) && !enabledBannerDismissed;
    const twoFactorStatus = mfaStatus ? (mfaStatus.enabled ? "Enabled" : "Not enabled") : "—";

    return (
        <Box className="component-container">
            <Typography component="h1" variant="h4" sx={{ mb: 0.5 }}>
                Profile
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2.5 }}>
                Your account and organisation information.
            </Typography>

            {showEnabledBanner ? (
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    onClose={() => setEnabledBannerDismissed(true)}
                    action={
                        <Button color="inherit" size="small" onClick={() => setManageMfaOpen(true)}>
                            Manage security
                        </Button>
                    }
                >
                    Keep your account secure. Two-factor authentication is enabled.
                </Alert>
            ) : null}

            {graceDeadline ? (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" component={RouterLink} to="/settings/mfa-setup">
                            Set up MFA
                        </Button>
                    }
                >
                    Please enable two-factor authentication before {graceDeadline}.
                </Alert>
            ) : null}

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={3} sx={{ p: 2.5, height: "100%" }}>
                        <Typography component="h2" variant="h6" gutterBottom>
                            Account
                        </Typography>
                        <ProfileField label="Email address" value={email} />
                        <Divider />
                        <ProfileField label="Organisation name" value={organisationName} />
                    </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={3} sx={{ p: 2.5, height: "100%" }}>
                        <Typography component="h2" variant="h6" gutterBottom>
                            Organisation
                        </Typography>
                        {organisationLoadError ? (
                            <Alert severity="warning">{organisationLoadError}</Alert>
                        ) : organisationSettings ? (
                            <>
                                <ProfileField
                                    label="Start of week"
                                    value={formatStartDayOfWeek(organisationSettings.startDayOfWeek)}
                                />
                                <Divider />
                                <ProfileField
                                    label="Start of year"
                                    value={formatStartMonth(organisationSettings.startMonth)}
                                />
                                <Divider />
                                <ProfileField
                                    label="Fiscal calendar"
                                    value={formatFiscalCalendar(organisationSettings.fiscalCalendarRuleSet)}
                                />
                            </>
                        ) : (
                            <Typography variant="body2">Loading…</Typography>
                        )}
                    </Paper>
                </Grid>

                <Grid size={{ xs: 12 }}>
                    <Paper elevation={3} sx={{ p: 2.5 }}>
                        <Typography component="h2" variant="h6" gutterBottom>
                            Security
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                                py: 2,
                                flexWrap: "wrap",
                            }}
                        >
                            <Typography variant="body1">Password</Typography>
                            <Button variant="outlined" onClick={() => setChangePasswordOpen(true)}>
                                Change password
                            </Button>
                        </Box>
                        <Divider />
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                                py: 2,
                                flexWrap: "wrap",
                            }}
                        >
                            <Typography variant="body1" sx={{ minWidth: 200 }}>
                                Two-factor authentication
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, flex: 1 }}>
                                {twoFactorStatus}
                            </Typography>
                            <Button variant="outlined" onClick={() => setManageMfaOpen(true)}>
                                Manage 2FA
                            </Button>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            <Dialog open={changePasswordOpen} onClose={handleChangePasswordClose} maxWidth="sm" fullWidth>
                <Box component="form" onSubmit={handleSubmit}>
                    <DialogTitle>Change password</DialogTitle>
                    <DialogContent>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            label="Old password"
                            type="password"
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                        />
                        <TextField
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            label="New password"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                        <TextField
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            label="Confirm new password"
                            type="password"
                            value={confirmNewPassword}
                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                            slotProps={{
                                htmlInput: {
                                    onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault(),
                                },
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleChangePasswordClose}>Cancel</Button>
                        <Button type="submit" variant="contained">
                            Change password
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>

            <Dialog open={manageMfaOpen} onClose={handleManageMfaClose} maxWidth="sm" fullWidth>
                <DialogTitle>Manage two-factor authentication</DialogTitle>
                <DialogContent>
                    {mfaLoadError ? (
                        <Alert severity="warning">{mfaLoadError}</Alert>
                    ) : mfaStatus ? (
                        <>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Status: <strong>{mfaStatus.enabled ? "Enabled" : "Not enabled"}</strong>
                                {mfaStatus.recoveryCodesRemaining != null && mfaStatus.enabled ? (
                                    <> — Recovery codes remaining: {mfaStatus.recoveryCodesRemaining}</>
                                ) : null}
                            </Typography>
                            {graceDeadline ? (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                    Please enable two-factor authentication before {graceDeadline}.
                                </Alert>
                            ) : null}
                            {!mfaStatus.enabled ? (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => navigate("/settings/mfa-setup")}
                                >
                                    Set up authenticator
                                </Button>
                            ) : newRecoveryCodes ? (
                                <>
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        Save these codes in a safe place. You will only see these recovery codes once,
                                        and each can be used once.
                                    </Typography>
                                    <Box component="ul" sx={{ fontFamily: "monospace", pl: 3, mb: 0 }}>
                                        {newRecoveryCodes.map((code) => (
                                            <li key={code}>{code}</li>
                                        ))}
                                    </Box>
                                </>
                            ) : (
                                <>
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        Enter a code from your authenticator app to regenerate recovery codes. Existing
                                        codes will stop working.
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        label="Authenticator code"
                                        value={regenCode}
                                        onChange={(e) => setRegenCode(e.target.value)}
                                    />
                                </>
                            )}
                        </>
                    ) : (
                        <Typography variant="body2">Loading…</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    {mfaStatus?.enabled && !newRecoveryCodes ? (
                        <Button onClick={handleRegenCodes} variant="contained" disabled={!regenCode.trim()}>
                            Regenerate recovery codes
                        </Button>
                    ) : null}
                    <Button onClick={handleManageMfaClose} variant={newRecoveryCodes ? "contained" : "text"}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={errorSnackbarOpen}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                autoHideDuration={6000}
                onClose={() => setErrorSnackbarOpen(false)}
            >
                <Alert onClose={() => setErrorSnackbarOpen(false)} severity="error" sx={{ width: "100%" }}>
                    {errorMessage}
                </Alert>
            </Snackbar>
            <Snackbar
                open={successSnackbarOpen}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                autoHideDuration={6000}
                onClose={() => setSuccessSnackbarOpen(false)}
            >
                <Alert onClose={() => setSuccessSnackbarOpen(false)} severity="success" sx={{ width: "100%" }}>
                    {successMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default ProfilePage;
